import {
  Table,
  TableRow,
  TableContainer,
  TableBody,
  TableHead,
  Collapse,
  Box,
  Toolbar,
  Paper,
} from '@mui/material';
import styled from 'styled-components';
import { CellWithBorder, CellWithBorderClassNames } from 'shared/styles';
import findIndex from 'lodash/findIndex';
import { conditionallyRenderElement } from 'utils';
import cn from 'classnames';
import { ReactNode } from 'react';
import { MergeExclusive, ValueOf } from 'type-fest';
import { ORDER_DIRECTION } from '@constants';

export const StyledTableRow = styled(TableRow)(({ theme }: any) => ({
  '&.clickable': {
    cursor: 'pointer',
    '&:hover': {
      td: {
        backgroundColor:
          theme.palette.mode === 'dark'
            ? 'rgba(59, 130, 246, 0.12) !important'
            : 'rgba(59, 130, 246, 0.06) !important',
      },
    },
    '&:active': {
      td: {
        backgroundColor:
          theme.palette.mode === 'dark'
            ? 'rgba(59, 130, 246, 0.18) !important'
            : 'rgba(59, 130, 246, 0.1) !important',
      },
    },
  },

  '&.has-hover-effect': {
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      td: {
        backgroundColor:
          theme.palette.mode === 'dark'
            ? 'rgba(59, 130, 246, 0.08) !important'
            : 'rgba(59, 130, 246, 0.04) !important',
      },
      // Use box-shadow instead of ::before to avoid layout shifts
      boxShadow:
        theme.palette.mode === 'dark'
          ? 'inset 3px 0 0 0 rgba(59, 130, 246, 0.8)'
          : 'inset 3px 0 0 0 rgba(37, 99, 235, 0.8)',
    },
  },
}));

type ColumnId = string;

/**
 *
 * TODO: Column should really be an for flexibility,
 * so need to go to other tables that use `useMuiTableBuilderV2` to make sure it uses the correct type
 *
 * Also update `HeaderCell` in Cells folder
 */
export type Column = MergeExclusive<
  ColumnId,
  {
    id: ColumnId;
    label?: string;
    tooltip?: ReactNode;
    sortDisabled?: boolean;
  }
>;

type RowType = string;

type DefaultCustomRowProps = Record<string, any>;

export interface TableSortProps {
  orderDirection: ValueOf<typeof ORDER_DIRECTION>;
  orderBy: string;
  /**
   * optional, because some table does not support sorting at all
   */
  onSort?: (columnId: ColumnId) => void;
}

export type ListItem<Data = Record<string, unknown>> = {
  /**
   * Unique key of thist ListItem
   */
  id: string;

  /**
   * Type of the row, must be included in each item in mainList
   */
  rowType: RowType;

  /**
   * Items that are grouped under this current list items
   * i.e: { listItems: [{id: child2}] }
   */
  listItems?: ListItem[];

  /**
   *
   * A record of string (which has to represent a valid column) and classname as string for that cell
   * Useful for adding border. Take a look at the classes that CellWithBorder support
   *
   * i.e: { ['header'1]: 'class1', ['header'2]: 'class2' }
   */
  cellClassNameByColumns?: Record<ColumnId, CellWithBorderClassNames[]>;

  /**
   * This prop must be provided if you want a row to be able to collapse and expand
   */
  collapseProps?: {
    shouldWrapWithCollapse: boolean;
    isCollapse: boolean;
  };

  /**
   * If true, render this row as a single full-width cell spanning all columns.
   * Useful for expandable "details" rows.
   */
  isFullWidthRow?: boolean;

  /**
   * Provide to have the hover effect on row
   * Default: false
   */

  disableRowHover?: boolean;

  /**
   * Row click action
   */
  onRowClick?: (event: React.MouseEvent) => void;

  /**
   * If hasStickyHeader is provided, the list item to acts as a sticky header will need this flag.
   * Note that only ONE (1) row can be a sticky header
   */
  isStickyHeader?: boolean;
} & Data;

export type CellProps<Data = any, CustomRowProps = DefaultCustomRowProps> = {
  _customRowProps: CustomRowProps;
  column: Column;
} & ListItem<Data>; // Should type inside the cell component

type MainList = {
  listItems: ListItem[];
};

export type TableConfigV2 = {
  /**
   * Header title of the table
   */
  tableHeaderTitle: string;

  /* ------------------------------ Sticky Header ----------------------------- */

  /**
   * If provided, one of the list item must have isStickyHeader = true
   */
  hasStickyHeader?: boolean;

  /* -------------------------------------------------------------------------- */

  /**
   *
   */
  tableMaxHeight?: string;

  /**
   * Determine how many columns will this table have
   */
  columns: Column[];

  /**
   * Setting the min width of each column
   */
  columnsMinWidth?: Record<ColumnId, number>;

  /**
   * An object of row types, this is to make sure devs dont forget to include rowType in each item of mainList
   */
  rowTypes: Record<RowType, RowType>;

  /**
   * The key must represent a row type that is present on rowTypes
   */
  cellsByRowTypesAndColumns: Record<
    RowType,
    Record<ColumnId, (props: CellProps) => Nullable<JSX.Element>>
  >;

  hideToolbar?: boolean;
};

export type UseMuiTableBuilderV2Props = TableConfigV2 & {
  isLoading?: boolean;

  /* ------------------------------- Main Header ------------------------------ */
  renderHeaderTools?: () => JSX.Element;

  /* -------------------------------- Header Left ------------------------------ */
  /**
   * Optional left-side tools next to the title (e.g., tabs)
   */
  renderHeaderLeftTools?: () => JSX.Element;

  /* ---------------------------------- Body ---------------------------------- */
  /**
   *
   */
  mainList: MainList;

  /**
   * Anything in this object will be presented on all rows
   */
  _customRowProps?: DefaultCustomRowProps;

  /* --------------------------------- Footer --------------------------------- */

  /**
   * Function to render the footer component's body
   */
  renderFooterOptions?: () => JSX.Element;
};

const defaultColumnMinWidth = 200;

const renderRow = ({
  listItem,
  rowTypes,
  columns,
  columnsMinWidth,
  cellsByRowTypesAndColumns,
  _customRowProps,
}: {
  listItem: ListItem;
  rowTypes: UseMuiTableBuilderV2Props['rowTypes'];
  columns: UseMuiTableBuilderV2Props['columns'];
  columnsMinWidth: UseMuiTableBuilderV2Props['columnsMinWidth'];
  cellsByRowTypesAndColumns: UseMuiTableBuilderV2Props['cellsByRowTypesAndColumns'];
  _customRowProps: UseMuiTableBuilderV2Props['_customRowProps'];
}) => {
  const {
    rowType,
    cellClassNameByColumns,
    collapseProps,
    disableRowHover = false,
    id,
    onRowClick,
    isFullWidthRow = false,
  } = listItem;

  if (!rowTypes[rowType]) return null;

  const { shouldWrapWithCollapse, isCollapse } = collapseProps || {};

  // If this row is collapsible and currently not expanded, skip rendering it
  if (shouldWrapWithCollapse && !isCollapse) return null;

  const rowKey = `${id}-${rowType}`;

  return (
    <StyledTableRow
      onClick={onRowClick}
      key={rowKey}
      className={cn({
        [rowType]: rowType,
        clickable: !!onRowClick,
        'has-hover-effect': !disableRowHover,
      })}
    >
      {(isFullWidthRow ? [columns[0]] : columns).map((column, columnIndex) => {
        const columnId = typeof column === 'object' ? column.id : column;

        const Cell = cellsByRowTypesAndColumns[rowType][columnId];
        const columnMinWidth = columnsMinWidth?.[columnId] ?? defaultColumnMinWidth;
        const cellClassName = (cellClassNameByColumns?.[columnId] || []).join(' ');

        const CellContent = shouldWrapWithCollapse ? (
          <Collapse in={isCollapse} timeout="auto" unmountOnExit>
            <Cell _customRowProps={_customRowProps} {...listItem} column={column} />
          </Collapse>
        ) : (
          <Cell _customRowProps={_customRowProps} {...listItem} column={column} />
        );

        const cellKey = `${rowKey}-${columnId}`;

        return (
          <CellWithBorder
            key={cellKey}
            colSpan={isFullWidthRow && columnIndex === 0 ? columns.length : undefined}
            sx={{
              backgroundColor: 'transparent',
              minWidth: `${columnMinWidth}px`,
            }}
            align="left"
            className={cellClassName}
          >
            {CellContent}
          </CellWithBorder>
        );
      })}
    </StyledTableRow>
  );
};

const getTableList = ({
  hasStickyHeader,
  mainList,
}: {
  hasStickyHeader: UseMuiTableBuilderV2Props['hasStickyHeader'];
  mainList: UseMuiTableBuilderV2Props['mainList'];
}) => {
  const { listItems } = mainList;

  const flatList = flattenList(listItems);

  if (hasStickyHeader) {
    const indexOfStickyHeader = findIndex(flatList, 'isStickyHeader');

    const tableHeadList: ListItem[] = flatList.slice(0, indexOfStickyHeader + 1);
    const tableBodyList: ListItem[] = flatList.slice(indexOfStickyHeader + 1);

    return {
      tableHeadList,
      tableBodyList,
    };
  }
  return {
    tableHeadList: [],
    tableBodyList: flatList,
  };
};

const useMuiTableBuilderV2 = ({
  tableMaxHeight = '500px',
  hasStickyHeader = false,
  columns,
  columnsMinWidth,
  cellsByRowTypesAndColumns,
  mainList,
  rowTypes,
  tableHeaderTitle,
  isLoading = false,
  _customRowProps = {},
  renderFooterOptions,
  renderHeaderTools,
  renderHeaderLeftTools,
  hideToolbar,
}: UseMuiTableBuilderV2Props) => {
  const { tableHeadList, tableBodyList } = getTableList({
    hasStickyHeader,
    mainList,
  });

  const TableComponent = (
    <Box>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.85)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(24px)',
          border: (theme) =>
            theme.palette.mode === 'dark'
              ? '1px solid rgba(148, 163, 184, 0.12)'
              : '1px solid rgba(226, 232, 240, 0.8)',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? '0 4px 24px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05) inset'
              : '0 4px 24px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? '0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.08) inset'
                : '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        {!hideToolbar ? (
          <Toolbar
            sx={{
              px: 4,
              py: 2.5,
              minHeight: '72px',
              borderBottom: (theme) =>
                theme.palette.mode === 'dark'
                  ? '1px solid rgba(148, 163, 184, 0.12)'
                  : '1px solid rgba(226, 232, 240, 0.6)',
              background: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(180deg, rgba(30, 41, 59, 0.4) 0%, transparent 100%)'
                  : 'linear-gradient(180deg, rgba(248, 250, 252, 0.6) 0%, transparent 100%)',
            }}
          >
            <Box display="flex" flexDirection="column" width="100%">
              {tableHeaderTitle ? (
                <Box
                  component={'h6'}
                  sx={{
                    fontSize: '1.125rem',
                    fontWeight: 700,
                    letterSpacing: '-0.01em',
                    background: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, #f8fafc 0%, #cbd5e1 100%)'
                        : 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  {tableHeaderTitle}
                </Box>
              ) : null}
              <Box display="flex" alignItems="center" mt={1}>
                <Box>{renderHeaderLeftTools ? renderHeaderLeftTools() : null}</Box>
                <Box ml="auto">{renderHeaderTools ? renderHeaderTools() : null}</Box>
              </Box>
            </Box>
          </Toolbar>
        ) : (
          <></>
        )}
        {conditionallyRenderElement({
          isLoading,
          error: undefined,
          content: (
            <>
              <TableContainer
                sx={{
                  maxHeight: tableMaxHeight,
                  overflow: 'auto',
                  // Improve scroll performance
                  willChange: 'scroll-position',
                  transform: 'translateZ(0)',
                  WebkitOverflowScrolling: 'touch',
                  '&::-webkit-scrollbar': {
                    width: '12px',
                    height: '12px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(15, 23, 42, 0.3)'
                        : 'rgba(241, 245, 249, 0.5)',
                    borderRadius: '8px',
                    margin: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(148, 163, 184, 0.3)'
                        : 'rgba(148, 163, 184, 0.4)',
                    borderRadius: '8px',
                    border: (theme) =>
                      theme.palette.mode === 'dark'
                        ? '2px solid rgba(15, 23, 42, 0.3)'
                        : '2px solid rgba(241, 245, 249, 0.5)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'rgba(148, 163, 184, 0.5)'
                          : 'rgba(100, 116, 139, 0.6)',
                    },
                  },
                }}
              >
                <Table
                  stickyHeader
                  sx={{
                    '& .MuiTableHead-root': {
                      '& .MuiTableCell-root': {
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(15, 23, 42, 0.98)'
                            : 'rgba(248, 250, 252, 0.98)',
                        backdropFilter: 'blur(12px)',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.8px',
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(203, 213, 225, 0.9)'
                            : 'rgba(51, 65, 85, 0.9)',
                        borderBottom: (theme) =>
                          theme.palette.mode === 'dark'
                            ? '2px solid rgba(148, 163, 184, 0.15)'
                            : '2px solid rgba(226, 232, 240, 0.8)',
                        py: 2,
                        px: 3,
                        whiteSpace: 'nowrap',
                        '&:first-of-type': {
                          pl: 4,
                        },
                        '&:last-of-type': {
                          pr: 4,
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: '1px',
                          background: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'linear-gradient(90deg, transparent 0%, rgba(148, 163, 184, 0.2) 50%, transparent 100%)'
                              : 'linear-gradient(90deg, transparent 0%, rgba(226, 232, 240, 0.8) 50%, transparent 100%)',
                        },
                        // Sticky column styling for header cells
                        '&.sticky': {
                          position: 'sticky',
                          left: 0,
                          zIndex: 3,
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            width: '1px',
                            background: (theme) =>
                              theme.palette.mode === 'dark'
                                ? 'linear-gradient(180deg, transparent 0%, rgba(148, 163, 184, 0.15) 50%, transparent 100%)'
                                : 'linear-gradient(180deg, transparent 0%, rgba(226, 232, 240, 0.8) 50%, transparent 100%)',
                          },
                        },
                      },
                    },
                    '& .MuiTableBody-root': {
                      '& .MuiTableRow-root': {
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'rgba(59, 130, 246, 0.08)'
                              : 'rgba(59, 130, 246, 0.04)',
                          transform: 'scale(1.001)',
                          '& .MuiTableCell-root': {
                            borderBottomColor: (theme) =>
                              theme.palette.mode === 'dark'
                                ? 'rgba(59, 130, 246, 0.15)'
                                : 'rgba(59, 130, 246, 0.1)',
                          },
                        },
                        '&:last-child .MuiTableCell-root': {
                          borderBottom: 'none',
                        },
                      },
                      '& .MuiTableCell-root': {
                        fontSize: '0.875rem',
                        py: 2.5,
                        px: 3,
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(226, 232, 240, 0.95)'
                            : 'rgba(30, 41, 59, 0.95)',
                        borderBottom: (theme) =>
                          theme.palette.mode === 'dark'
                            ? '1px solid rgba(148, 163, 184, 0.08)'
                            : '1px solid rgba(226, 232, 240, 0.6)',
                        transition: 'all 0.2s ease',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '400px', // Default max-width to prevent overflow
                        '&:first-of-type': {
                          pl: 4,
                        },
                        '&:last-of-type': {
                          pr: 4,
                        },
                      },
                    },
                  }}
                >
                  <TableHead>
                    {tableHeadList.map((listItem) =>
                      renderRow({
                        listItem,
                        rowTypes,
                        cellsByRowTypesAndColumns,
                        _customRowProps,
                        columns,
                        columnsMinWidth,
                      }),
                    )}
                  </TableHead>
                  <TableBody>
                    {tableBodyList.map((listItem) =>
                      renderRow({
                        listItem,
                        rowTypes,
                        cellsByRowTypesAndColumns,
                        _customRowProps,
                        columns,
                        columnsMinWidth,
                      }),
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Footer */}
              {renderFooterOptions ? (
                <Box
                  display={'flex'}
                  alignItems="center"
                  justifyContent={'flex-end'}
                  minHeight="64px"
                  px={4}
                  py={2}
                  sx={{
                    borderTop: (theme) =>
                      theme.palette.mode === 'dark'
                        ? '1px solid rgba(148, 163, 184, 0.12)'
                        : '1px solid rgba(226, 232, 240, 0.6)',
                    background: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'linear-gradient(0deg, rgba(30, 41, 59, 0.4) 0%, transparent 100%)'
                        : 'linear-gradient(0deg, rgba(248, 250, 252, 0.6) 0%, transparent 100%)',
                    '& .MuiTablePagination-root': {
                      color: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'rgba(226, 232, 240, 0.9)'
                          : 'rgba(51, 65, 85, 0.9)',
                    },
                    '& .MuiTablePagination-actions button': {
                      color: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'rgba(226, 232, 240, 0.7)'
                          : 'rgba(51, 65, 85, 0.7)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(59, 130, 246, 0.15)'
                            : 'rgba(59, 130, 246, 0.1)',
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(59, 130, 246, 1)'
                            : 'rgba(37, 99, 235, 1)',
                      },
                      '&.Mui-disabled': {
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(226, 232, 240, 0.3)'
                            : 'rgba(51, 65, 85, 0.3)',
                      },
                    },
                  }}
                >
                  {renderFooterOptions()}
                </Box>
              ) : null}
            </>
          ),
        })}
      </Paper>
    </Box>
  );

  return {
    TableComponent,
  };
};

export default useMuiTableBuilderV2;

const flattenList = (listItems: ListItem[]) => {
  const flatList: ListItem[] = [];
  for (const item of listItems) {
    const { listItems } = item || {};

    flatList.push(item);

    if (listItems) {
      // Has list items (children), build them
      flatList.push(...flattenList(listItems));
    }
  }

  return flatList;
};
